'use client';

import * as React from 'react';
import {
  arrow, autoUpdate, flip, FloatingNode, FloatingPortal, FloatingTree, hide, offset, shift, useClick,
  useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useHover, useInteractions, useRole, type Placement,
} from '@floating-ui/react';
import { cn } from '../utils';

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';

export interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  side?: PopoverSide;
  align?: PopoverAlign;
  /** Interaction mode. Default: 'both' (hover + click). */
  openOn?: 'hover' | 'click' | 'both';
  /** Fixed content width in px. Default 320. */
  width?: number;
  /** Controlled open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: React.ReactNode;
  ariaLabel?: string;
  /** Extra classes on the content container. */
  className?: string;
  /** Extra inline styles on the content container — merged AFTER the positioning
   *  styles so callers can override e.g. `maxHeight` / `overflow` without
   *  fighting class-merge precedence. Position/transform stay reserved.
   *
   *  Prefer `maxContentHeight` (below) for the common "scrollable popover"
   *  case — this prop predates that one and stays for callers that need
   *  bespoke styling. */
  contentStyle?: React.CSSProperties;
  /** Cap the popover content height and make it vertically scrollable. When
   *  set, the primitive switches from the default `overflow-hidden`
   *  rounded-corner clip to `overflow-y-auto`, reserves the scrollbar
   *  gutter from first paint (so first-scroll doesn't reflow inner rows),
   *  and contains overscroll so wheel events don't drag a parent drawer.
   *
   *  Accepts CSS length strings (`'20rem'`, `'320px'`) or numeric px. Use
   *  this instead of reaching past the primitive via `contentStyle` —
   *  centralizing the scrollbar/overscroll rules means every scrollable
   *  popover behaves the same. */
  maxContentHeight?: number | string;
  /** Wrapper className for the trigger. Defaults to `inline-flex`. */
  wrapperClassName?: string;
  /** Show a pointer arrow on the popover. Default true. */
  showArrow?: boolean;
  /** Hover-to-open delay in ms. Default 0 — popover opens immediately on hover.
   *  Bump on surfaces where you don't want incidental cursor brushes to trigger
   *  the popover (e.g. the EV upsell that lives on every blurred edge). */
  openDelayMs?: number;
}

const HOVER_HIDE_GRACE = 80;
// Gap between trigger and content, and the viewport padding flip/shift keep.
const GAP = 6;
const PAD = 8;

// Floating UI owns the popover: placement (flip and shift inside the viewport, follow the trigger, hide with it),
// hover, click and dismissal (Escape, an outside click), roles, and nesting: a popover opened inside another is a child
// in its tree, so a click inside the child keeps the parent open and a click elsewhere closes the child only.
export const Popover: React.FC<PopoverProps> = props => (
  useFloatingParentNodeId() === null ? <FloatingTree><PopoverNode {...props} /></FloatingTree> : <PopoverNode {...props} />
);

const PopoverNode: React.FC<PopoverProps> = ({
  trigger,
  children,
  side = 'bottom',
  align = 'center',
  openOn = 'both',
  width = 320,
  open: openProp,
  onOpenChange,
  title,
  ariaLabel,
  className,
  contentStyle,
  maxContentHeight,
  wrapperClassName,
  showArrow = true,
  openDelayMs = 0,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const controlled = openProp !== undefined;
  const nodeId = useFloatingNodeId();
  const [arrowEl, setArrowEl] = React.useState<HTMLSpanElement | null>(null);

  const { refs: { setReference, setFloating }, context, floatingStyles, placement: actual, middlewareData, isPositioned } = useFloating({
    nodeId,
    open,
    onOpenChange: (next, event, reason) => {
      // An outside click that closes it never reaches what was clicked (a table row would open its overlay).
      if (reason === 'outside-press') event?.stopPropagation();
      if (!controlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    placement: (align === 'center' ? side : `${side}-${align}`) as Placement,
    strategy: 'fixed',
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [offset(GAP), flip({ padding: PAD }), shift({ padding: PAD }), arrow({ element: arrowEl }), hide()],
  });

  const hoverEnabled = openOn === 'hover' || openOn === 'both';
  const clickEnabled = openOn === 'click' || openOn === 'both';
  const { getReferenceProps, getFloatingProps } = useInteractions([
    // Opens on the pointer entering, not on a move over the trigger: a popover clicked shut stays shut until it leaves.
    useHover(context, { enabled: hoverEnabled, move: false, delay: { open: openDelayMs, close: HOVER_HIDE_GRACE } }),
    // A click closes it even when hover opened it.
    useClick(context, { enabled: clickEnabled, stickIfOpen: false }),
    // An outside click closes it at its target, stopped there (onOpenChange) so the page's handlers (a row) never see it.
    useDismiss(context, { outsidePressEvent: 'click', capture: true }),
    useRole(context, { role: 'dialog' }),
  ]);

  // Unmounting while open (its row left the list) closes it: say so, as a close would.
  const closeOnUnmount = React.useEffectEvent(() => { if (open) onOpenChange?.(false); });
  React.useEffect(() => () => closeOnUnmount(), []);

  // Intentionally no auto-focus on open: hover-driven popovers shouldn't steal
  // focus. Keyboard users can Tab into content naturally; Escape closes.

  const placement = actual.split('-')[0] as PopoverSide;
  const shown = isPositioned && !middlewareData.hide?.referenceHidden;

  return (
    <FloatingNode id={nodeId}>
      <span
        ref={setReference}
        className={wrapperClassName ?? 'inline-flex'}
        aria-label={ariaLabel}
        {...getReferenceProps({
          // The trigger's own click and Enter/Space stop here: a row behind it must not take them.
          onClick: e => { e.preventDefault(); e.stopPropagation(); },
          onKeyDown: e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); },
        })}
      >
        {trigger}
      </span>

      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            aria-label={ariaLabel}
            tabIndex={-1}
            className={cn(
              // `bg-bg dark:bg-bg-secondary` matches the rest of the app's
              // elevated surfaces (game-context chip, drawer body) — the
              // previous `bg-bg-quaternary` was visibly lighter than every
              // other dark-mode surface and looked out of place.
              'fixed z-[1070] rounded-lg shadow-xl bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary text-fg dark:text-text-primary',
              // `overflow-hidden` clips rounded corners cleanly for static
              // popovers. When `maxContentHeight` is set, we need vertical
              // scrolling instead, so switch to `overflow-y-auto` (with
              // x-axis still clipped) and reserve the scrollbar gutter so
              // first-scroll doesn't reflow inner rows.
              maxContentHeight === undefined
                ? 'overflow-hidden'
                : 'overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] [overscroll-behavior:contain]',
              'pointer-events-auto',
              'transition-[opacity,transform] duration-[90ms] ease-out motion-reduce:transition-none',
              className,
            )}
            style={{
              ...floatingStyles,
              width,
              opacity: shown ? 1 : 0,
              visibility: middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
              pointerEvents: shown ? 'auto' : 'none',
              transform: shown ? 'translateY(0) scale(1)' : `translateY(${placement === 'top' ? 2 : -2}px) scale(0.985)`,
              transformOrigin: placement === 'top' ? 'bottom center' : placement === 'bottom' ? 'top center' : placement === 'left' ? 'right center' : 'left center',
              // Caller-supplied overrides go above the dedicated props so
              // e.g. `maxHeight` / `overflow` can be set without fighting
              // `cn()` class merging. Position keys above are spread first
              // so callers can't accidentally clobber positioning.
              ...contentStyle,
              // `maxContentHeight` is the canonical scrollable-popover API
              // and wins over both `contentStyle.maxHeight` and any class
              // merge — encourages new callers to use it instead of the
              // bespoke `contentStyle` escape hatch.
              ...(maxContentHeight !== undefined && {
                maxHeight: typeof maxContentHeight === 'number' ? `${maxContentHeight}px` : maxContentHeight,
              }),
            }}
            {...getFloatingProps()}
          >
            {title}
            {children}
            {showArrow && (
              <span ref={setArrowEl} className={arrowStyle(placement)} style={{ left: middlewareData.arrow?.x, top: middlewareData.arrow?.y }} aria-hidden />
            )}
          </div>
        </FloatingPortal>
      )}
    </FloatingNode>
  );
};

// The arrow's offset along the edge comes from Floating UI (it tracks the trigger when the content shifts).
function arrowStyle(placement: PopoverSide): string {
  const base = 'absolute w-2 h-2 rotate-45 bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary';
  switch (placement) {
    case 'bottom': return cn(base, '-top-1 border-b-0 border-r-0');
    case 'top':    return cn(base, '-bottom-1 border-t-0 border-l-0');
    case 'right':  return cn(base, '-left-1 border-t-0 border-r-0');
    case 'left':   return cn(base, '-right-1 border-b-0 border-l-0');
  }
}
